import {
  Component,
  Show,
  createSignal,
  createEffect,
  createResource,
} from "solid-js";
import { useNavigate } from "@solidjs/router";
import { Modal, Button } from "../ui";
import { ReportIssueModal } from "../chat/ReportIssueModal";
import {
  quotaPaywall,
  quotaRequestOpen,
  dismissQuotaPaywall,
  openQuotaRequest,
  closeQuotaRequest,
  type QuotaScope,
} from "../../stores/quotaPaywallStore";
import { tenantRole, currentUser } from "../../stores/currentUserStore";
import { isTenantAdmin, tenantRoleFor } from "../../utils/permissions";
import { getTenantBilling, createPackCheckout } from "../../api/tenants";
import {
  paywallPackOffer,
  upgradeCta,
  storePackBaseline,
  classifyBillingError,
  PAYWALL_PACK,
} from "../../utils/billing";
import type { TenantId } from "../../api/types";

const TENANT_REQUEST_TITLE = "Request: raise the AI quota for my organization";
const projectRequestTitle = (name: string | undefined) =>
  name
    ? `Request: raise the AI budget for project ${name}`
    : "Request: raise the AI budget for this project";
const formatResume = (at: Date) =>
  at.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

/**
 * FLOW-795: interstitial for a 429 quota refusal on a user-initiated AI
 * action. Mounted once in AppShell. FLOW-910: the refusal's scope decides the
 * copy — the organization's caps (`QUOTA_EXHAUSTED`) or one project's monthly
 * budget (`PROJECT_BUDGET_EXHAUSTED`). The project is whatever AppShell has
 * selected, which is the project the refused action ran against; with none
 * selected the copy stays generic and the settings trip is omitted. FLOW-928:
 * the $10/+5M add-on pack is offered only against a monthly tenant refusal,
 * because pack credit lands on the UTC month alone and so clears neither a
 * daily-burst cap nor a project ceiling.
 */
export const QuotaPaywallModal: Component<{
  projectId?: string;
  projectName?: string;
  projectTenantId?: TenantId;
}> = (props) => {
  const navigate = useNavigate();
  const isProject = () => quotaPaywall()?.scope === "project";
  const subject = () => props.projectName ?? "this project";
  const title = () =>
    isProject()
      ? "AI is paused for this project"
      : "AI is paused for your organization";

  // requestTitle is captured when the request form opens, not read live,
  // because openQuotaRequest() nulls quotaPaywall() before the form renders.
  const [lastScope, setLastScope] = createSignal<QuotaScope>("tenant");
  createEffect(() => {
    const scope = quotaPaywall()?.scope;
    if (scope) setLastScope(scope);
  });
  const requestTitle = () =>
    lastScope() === "project"
      ? projectRequestTitle(props.projectName)
      : TENANT_REQUEST_TITLE;

  // Only an org admin can act on a plan (FLOW-700): `/settings/billing`
  // answers 403 for anyone else, so offering the trip would be a dead end.
  // Each trip is gated on the tenant that scope is actually about, since
  // FLOW-931: "View plans" (tenant scope) uses `tenantRole()`, the caller's
  // PRIMARY tenant, because that's the tenant the org-wide quota is charged
  // against. "View project AI budget" (project scope) instead uses
  // `tenantRoleFor(..., props.projectTenantId)` — the selected project's own
  // tenant, which need not be the caller's primary one. Using the primary
  // tenant for the project trip would wrongly hide it from an admin of the
  // project's (non-primary) tenant and wrongly show it to a primary-tenant
  // admin who has no role in the project's tenant at all.
  const viewPlans = () => {
    navigate("/settings/billing");
    dismissQuotaPaywall();
  };
  const viewProjectBudget = () => {
    navigate(`/projects/${props.projectId}/settings`);
    dismissQuotaPaywall();
  };

  // FLOW-928: the tenant the refusal is about, and only when this caller may
  // act on its billing — FLOW-931's split, reused. `undefined` means no
  // request at all, which is load-bearing for a non-admin: a request would
  // 403-noise the console and probe another org's commercial state.
  const ctaTenantId = (): TenantId | undefined =>
    isProject()
      ? isTenantAdmin(
          tenantRoleFor(currentUser()?.tenants, props.projectTenantId),
        )
        ? props.projectTenantId
        : undefined
      : isTenantAdmin(tenantRole())
        ? currentUser()?.tenants?.[0]?.tenant_id
        : undefined;

  const [billing] = createResource(
    () => (quotaPaywall() !== null ? ctaTenantId() : undefined),
    (id) => getTenantBilling(id),
  );
  const loaded = () => (billing.state === "ready" ? billing() : undefined);
  const packOffer = () =>
    paywallPackOffer(loaded(), quotaPaywall()?.cap ?? null);
  const upgrade = () => (ctaTenantId() ? upgradeCta(loaded()) : null);

  const [packBusy, setPackBusy] = createSignal(false);
  const [packError, setPackError] = createSignal("");

  // This component is mounted once in AppShell, so a purchase error outlives
  // the dialog that reported it: without this, a refusal dismissed after a
  // failed purchase would reopen later already showing the stale red alert.
  createEffect(() => {
    if (quotaPaywall() === null) setPackError("");
  });

  const buyPack = async () => {
    const tenant = ctaTenantId();
    if (!tenant) return;
    setPackError("");
    setPackBusy(true);
    try {
      const session = await createPackCheckout(tenant, { pack: PAYWALL_PACK });
      // Baseline must be stored before the navigation: the Stripe webhook
      // routinely lands before the browser round trip back to the billing
      // page, and a baseline taken after that would already include the
      // credit, falsely reporting no growth (FLOW-851).
      storePackBaseline(loaded()?.pack_tokens_this_month ?? 0);
      window.location.assign(session.url);
    } catch (err) {
      const outcome = classifyBillingError(err);
      setPackError(
        outcome.kind === "unavailable"
          ? "Token packs are not available in this environment."
          : outcome.kind === "stale"
            ? "Your subscription changed — reload and try again."
            : outcome.message,
      );
    } finally {
      setPackBusy(false);
    }
  };

  return (
    <>
      <Modal
        open={quotaPaywall() !== null}
        onClose={dismissQuotaPaywall}
        title={title()}
        size="md"
      >
        <div class="flex flex-col gap-3 text-sm text-ink-2">
          <Show
            when={isProject()}
            fallback={
              <p>
                This action needs the full AI model, and your organization has
                used its AI token budget for this period. Deep planning,
                breakdowns and chat are on hold until it resets.
              </p>
            }
          >
            <p>
              This action needs the full AI model, and {subject()} has used its
              AI token budget for this month. Deep planning, breakdowns and chat
              are on hold for this project only until it resets.
            </p>
          </Show>
          <Show
            when={quotaPaywall()?.resumesAt}
            fallback={<p>AI features resume when the budget resets.</p>}
          >
            {(at) => (
              <Show
                when={isProject()}
                fallback={<p>AI features resume on {formatResume(at())}.</p>}
              >
                <p>
                  AI features for this project resume on {formatResume(at())}.
                </p>
              </Show>
            )}
          </Show>
          <p>
            Everything else keeps working: boards, backlog, items and documents
            are unaffected, and your work is saved.
          </p>
          <div class="flex flex-wrap justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={dismissQuotaPaywall}>
              Continue without AI
            </Button>
            <Show
              when={isProject()}
              fallback={
                <Show when={upgrade()}>
                  {(label) => (
                    <Button variant="secondary" onClick={viewPlans}>
                      {label()}
                    </Button>
                  )}
                </Show>
              }
            >
              <Show when={props.projectId}>
                <Show
                  when={isTenantAdmin(
                    tenantRoleFor(
                      currentUser()?.tenants,
                      props.projectTenantId,
                    ),
                  )}
                >
                  <Button variant="secondary" onClick={viewProjectBudget}>
                    View project AI budget
                  </Button>
                </Show>
              </Show>
              <Show when={upgrade()}>
                {(label) => (
                  <Button variant="secondary" onClick={viewPlans}>
                    {label()}
                  </Button>
                )}
              </Show>
            </Show>
            <Show
              when={packOffer()}
              fallback={
                <Button variant="primary" onClick={openQuotaRequest}>
                  Request more AI quota
                </Button>
              }
            >
              {(offer) => (
                <>
                  <Button variant="secondary" onClick={openQuotaRequest}>
                    Request more AI quota
                  </Button>
                  <Button
                    variant="primary"
                    loading={packBusy()}
                    onClick={buyPack}
                  >
                    {offer().label}
                  </Button>
                </>
              )}
            </Show>
          </div>
          <Show when={packError()}>
            {(message) => (
              <p role="alert" class="text-sm text-signal-red">
                {message()}
              </p>
            )}
          </Show>
        </div>
      </Modal>
      {/* Gated so a fresh instance is created per open: ReportIssueModal
          captures initialTitle into its own signal once at creation, and
          the scope (and so the title) can differ between opens. */}
      <Show when={quotaRequestOpen()}>
        <ReportIssueModal
          open={quotaRequestOpen()}
          onClose={closeQuotaRequest}
          initialTitle={requestTitle()}
        />
      </Show>
    </>
  );
};
